// Logs — opened when something broke.
//
// Moved off /reports, which was mixing money with machinery: the error log sat
// beside the financials despite being read at a different time, for a different
// reason, by someone in a different frame of mind. A page you open in a panic
// should not be a tab away from a page you open to plan.
//
// Four views, in the order you would actually reach for them: what threw, what
// happened, what Stripe told us, and which version was running at the time.
// Each loads only when its tab is opened — the event log alone reads eight
// collections, and nobody visiting the error log wants to wait for that.
import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, query, orderBy, doc, deleteDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import ChangelogDialog from '../components/ChangelogDialog';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsIcon from '@mui/icons-material/Payments';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

const EV_META = {
  registration: { label: 'Registration', bg: '#E8F0FE', color: '#1A56DB' },
  purchase:     { label: 'Purchase',     bg: '#E9F7EF', color: '#1E6B3C' },
  checkin:      { label: 'Check-in',     bg: '#FEF3E2', color: '#9C4E0E' },
  reservation:  { label: 'Reservation',  bg: '#FCF6E8', color: '#9C7A1E' },
  waitlist:     { label: 'Wait-list',    bg: '#F5EEF8', color: '#7D3C98' },
  tournament:   { label: 'Tournament',   bg: '#FDEDEC', color: '#922B21' },
  event:        { label: 'Event',        bg: '#EAF2F8', color: '#1A5276' },
  email:        { label: 'Email',        bg: '#FEF5E7', color: '#7E5109' },
  activity:     { label: 'Activity',     bg: '#F4F6F7', color: '#555' },
  class:        { label: 'Class',        bg: '#E8F6F3', color: '#117864' },
  memberBooking:{ label: 'Member Booking', bg: '#EBF0FA', color: '#2E86C1' },
  user:         { label: 'User',         bg: '#FDF2E9', color: '#AF601A' },
  athlete:      { label: 'Athlete',      bg: '#FDEDF1', color: '#A93254' },
};

const ts = (v) => (v?.seconds ? v.seconds * 1000 : (v ? new Date(v).getTime() : 0));

// A Firestore value as the detail view should read it: timestamps as dates,
// objects as indented JSON, empty as an em dash rather than a blank line.
const fmtValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object' && typeof v.seconds === 'number' && typeof v.nanoseconds === 'number') {
    return new Date(v.seconds * 1000).toLocaleString();
  }
  if (Array.isArray(v)) {
    return v.length
      ? v.map(x => (x && typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')
      : '—';
  }
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
};

// Long fields (a base64 photo, a stored payload) would bury everything else.
const truncate = (s, max = 400) => (s.length > max ? `${s.slice(0, max)}… (${s.length} chars)` : s);

export default function Logs() {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const _q = search.trim().toLowerCase();
  const qMatch = (...vals) => !_q || vals.some(v => String(v || '').toLowerCase().includes(_q));

  // ── Error log ──────────────────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // ── Event log ──────────────────────────────────────────────────────────
  const [evUsers, setEvUsers] = useState([]);
  const [evRegs, setEvRegs] = useState([]);
  const [evActivity, setEvActivity] = useState([]);
  const [evEvents, setEvEvents] = useState([]);
  const [evAttendance, setEvAttendance] = useState([]);
  const [evPurchases, setEvPurchases] = useState([]);
  const [evAthletes, setEvAthletes] = useState([]);
  const [evClasses, setEvClasses] = useState([]);
  const [evLoaded, setEvLoaded] = useState(false);
  const [evLoading, setEvLoading] = useState(false);
  // Empty selection = show all types; selecting chips narrows to just those.
  const [evTypes, setEvTypes] = useState([]);
  // A row carries the document it was derived from, so opening one shows the
  // record itself rather than the sentence the timeline made out of it.
  const [selectedEvent, setSelectedEvent] = useState(null);

  // ── Version log ────────────────────────────────────────────────────────
  // Nothing writes to versionLog yet. The tab exists so there is somewhere for
  // a release to be recorded when the deploy starts stamping one, and so that
  // "which build was live when this broke" has an answer next to the errors
  // that raise the question.
  const [versions, setVersions] = useState([]);
  // The table shows a one-line summary; the full section for a release lives
  // on the same doc, so a row opens it rather than refetching anything.
  const [openRelease, setOpenRelease] = useState(null);
  const [verLoading, setVerLoading] = useState(false);
  const [verLoaded, setVerLoaded] = useState(false);

  // ── Stripe webhook log ─────────────────────────────────────────────────
  const [whEvents, setWhEvents] = useState([]);
  const [whLoading, setWhLoading] = useState(false);
  const [whLoaded, setWhLoaded] = useState(false);
  const [whReplaying, setWhReplaying] = useState(null);
  const [whMsg, setWhMsg] = useState('');

  useEffect(() => {
    if (tab !== 0 || logsLoaded || logsLoading) return;
    setLogsLoading(true);
    getDocs(query(collection(db, 'logs'), orderBy('timestamp', 'desc')))
      .then(snap => { setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLogsLoaded(true); })
      .catch(console.error)
      .finally(() => setLogsLoading(false));
  }, [tab, logsLoaded, logsLoading]);

  useEffect(() => {
    if (tab !== 1 || evLoaded || evLoading) return;
    setEvLoading(true);
    Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'registrations')),
      getDocs(collection(db, 'activity')),
      getDocs(query(collection(db, 'events'), orderBy('timestamp', 'desc'))).catch(() => ({ docs: [] })),
      getDocs(collection(db, 'attendance')),
      getDocs(collection(db, 'purchases')),
      getDocs(collection(db, 'athletes')),
      getDocs(collection(db, 'classes')),
    ])
      .then(([u, r, a, ev, at, p, ath, cl]) => {
        const rows = (s) => s.docs.map(d => ({ id: d.id, ...d.data() }));
        setEvUsers(rows(u)); setEvRegs(rows(r)); setEvActivity(rows(a)); setEvEvents(rows(ev));
        setEvAttendance(rows(at)); setEvPurchases(rows(p)); setEvAthletes(rows(ath)); setEvClasses(rows(cl));
        setEvLoaded(true);
      })
      .catch(err => console.error('Event log load failed:', err))
      .finally(() => setEvLoading(false));
  }, [tab, evLoaded, evLoading]);

  useEffect(() => {
    if (tab !== 3 || verLoaded || verLoading) return;
    setVerLoading(true);
    getDocs(query(collection(db, 'versionLog'), orderBy('releasedAt', 'desc')))
      .then(snap => setVersions(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => setVersions([]))   // collection does not exist yet
      .finally(() => { setVerLoaded(true); setVerLoading(false); });
  }, [tab, verLoaded, verLoading]);

  useEffect(() => {
    if (tab === 2 && !whLoaded && !whLoading) loadWebhookLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const deleteLog = async (id) => {
    await deleteDoc(doc(db, 'logs', id));
    setLogs(prev => prev.filter(l => l.id !== id));
  };

  const clearAllLogs = async () => {
    if (!window.confirm('Delete all logs?')) return;
    await Promise.all(logs.map(l => deleteDoc(doc(db, 'logs', l.id))));
    setLogs([]);
  };

  async function loadWebhookLog() {
    setWhLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'stripeWebhookLog'), orderBy('receivedAt', 'desc')));
      const rows = snap.docs.map(d => {
        const w = { id: d.id, ...d.data() };
        // Older log docs don't carry customerId directly; recover it from the
        // stored raw payload (subscription/invoice events reference customers
        // by cus_ id only, so customerEmail is blank for them).
        if (!w.customerId && w.payload) {
          try { w.customerId = JSON.parse(w.payload)?.data?.object?.customer || ''; } catch (e) { /* ignore */ }
        }
        return w;
      });
      setWhEvents(rows);
      setWhLoaded(true);

      // Users and athletes map cus_ ids to people. Loaded here rather than with
      // the event log, because this tab is often the only one opened.
      let usersList = evUsers;
      let athList = evAthletes;
      if (usersList.length === 0 || athList.length === 0) {
        const [uSnap, aSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'athletes')),
        ]);
        usersList = uSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        athList = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setEvUsers(usersList);
        setEvAthletes(athList);
      }

      // Any rows still unresolvable locally: ask Stripe for the customer email.
      // The CF writes emails back onto the log docs, so this is one-time per customer.
      const unresolved = rows
        .filter(w => !w.customerEmail && typeof w.customerId === 'string' && w.customerId.startsWith('cus_'))
        .filter(w => !usersList.some(x => x.stripeCustomerId === w.customerId)
                  && !athList.some(x => x.stripeCustomerId === w.customerId));
      if (unresolved.length > 0) {
        try {
          const resolve = httpsCallable(getFunctions(), 'resolveWebhookCustomers');
          const r = await resolve({ items: unresolved.slice(0, 100).map(w => ({ docId: w.id, customerId: w.customerId })) });
          const emails = r.data?.emails || {};
          setWhEvents(prev => prev.map(w => (!w.customerEmail && emails[w.customerId])
            ? { ...w, customerEmail: emails[w.customerId] } : w));
        } catch (e) { console.warn('customer email resolution failed:', e.message); }
      }
    } catch (err) {
      console.error('Webhook log load failed:', err);
    }
    setWhLoading(false);
  }

  // Resolve a webhook row to a display name: payload email first, then match
  // the Stripe customer id against users/athletes, then fall back to the raw id.
  const whCustomer = (w) => {
    if (w.customerEmail) return w.customerEmail;
    if (!w.customerId || typeof w.customerId !== 'string') return '';
    const u = evUsers.find(x => x.stripeCustomerId === w.customerId);
    if (u) return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || w.customerId;
    const a = evAthletes.find(x => x.stripeCustomerId === w.customerId);
    if (a) return `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || w.customerId;
    return w.customerId;
  };

  const backfillWebhookLog = async () => {
    setWhLoading(true);
    setWhMsg('');
    try {
      const backfill = httpsCallable(getFunctions(), 'backfillStripeWebhookLog');
      const r = await backfill();
      setWhMsg(`Backfill complete: ${r.data?.imported ?? 0} imported, ${r.data?.skipped ?? 0} already logged.`);
      loadWebhookLog();
    } catch (e) {
      setWhMsg('Backfill failed: ' + (e.message || e));
      setWhLoading(false);
    }
    setTimeout(() => setWhMsg(''), 8000);
  };

  const replayWebhook = async (eventId) => {
    setWhReplaying(eventId);
    setWhMsg('');
    try {
      const replay = httpsCallable(getFunctions(), 'replayStripeWebhook');
      const r = await replay({ eventId });
      setWhMsg(r.data?.ok ? 'Replay succeeded — refreshing…' : `Replay returned HTTP ${r.data?.statusCode}`);
      setTimeout(loadWebhookLog, 1500);
    } catch (e) {
      setWhMsg('Replay failed: ' + (e.message || e));
    }
    setWhReplaying(null);
    setTimeout(() => setWhMsg(''), 5000);
  };

  // Normalize everything into one timeline. Attendance docs can yield several
  // events (joined wait-list, reserved, checked in) from their timestamps.
  const buildEvents = () => {
    const events = [];
    const classNameOf = (id) => {
      const c = evClasses.find(x => x.id === id);
      return c ? (c.eventName || c.sessionType || 'class') : 'class';
    };

    evAttendance.forEach(a => {
      const who = a.athleteName || a.athleteEmail || 'Unknown';
      const cls = classNameOf(a.classId);
      // One attendance doc can yield three rows, so the source is the same
      // record for each — the detail view says which moment opened it.
      const src = { collection: 'attendance', id: a.id, doc: a };
      if (a.waitlistedAt) events.push({ ts: ts(a.waitlistedAt), type: 'waitlist', who, desc: `joined the wait-list for ${cls}${a.date ? ` (${a.date})` : ''}`, src });
      if (a.reservedAt) events.push({ ts: ts(a.reservedAt), type: 'reservation', who, desc: `reserved a spot in ${cls}${a.date ? ` (${a.date})` : ''}${a.promotedFromWaitlist ? ' (promoted from wait-list)' : ''}`, src });
      if (a.checkedInAt) events.push({ ts: ts(a.checkedInAt), type: 'checkin', who, desc: `checked in to ${cls}${a.date ? ` (${a.date})` : ''}${a.type === 'coach' ? ' as coach' : ''}`, src });
    });

    evPurchases.forEach(p => {
      const t = ts(p.purchaseDate) || ts(p.createdAt);
      if (!t) return;
      // Resolve the buyer: the doc's own fields first, then the linked athlete
      // record, then the linked user record (older purchases often only carry ids).
      const ath = p.athleteId ? evAthletes.find(x => x.id === p.athleteId || x.athleteId === p.athleteId) : null;
      const usr = p.userId ? evUsers.find(x => x.id === p.userId || x.uid === p.userId) : null;
      const who = p.athleteName
        || (ath ? [ath.firstName, ath.lastName].filter(Boolean).join(' ') : '')
        || (usr ? [usr.firstName, usr.lastName].filter(Boolean).join(' ') : '')
        || p.email || p.customerEmail || ath?.email || usr?.email
        || 'Unknown';
      const buyerEmail = p.email || p.customerEmail || ath?.email || usr?.email || '';
      events.push({ ts: t, type: 'purchase', who,
        desc: `purchased ${p.planName || 'a plan'} — $${(p.amountPaid || 0).toFixed(2)}${p.paymentStatus && p.paymentStatus !== 'paid' ? ` (${p.paymentStatus})` : ''}${buyerEmail && buyerEmail !== who ? ` — ${buyerEmail}` : ''}`,
        src: { collection: 'purchases', id: p.id, doc: p } });
    });

    evUsers.forEach(u => {
      const t = ts(u.registrationDate) || ts(u.createdAt);
      if (!t) return;
      const who = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unknown';
      const mini = u.registrationLevel === 'mini';
      const alias = !!u.mergedIntoUid;
      events.push({ ts: t, type: 'registration', who,
        desc: alias ? `linked a new sign-in email (${u.email || ''})` : `registered${mini ? ' via mini-reg (day pass)' : ''}${u.email ? ` — ${u.email}` : ''}`,
        src: { collection: 'users', id: u.id, doc: u } });
    });

    evRegs.forEach(r => {
      const t = ts(r.createdAt) || ts(r.timestamp) || ts(r.registeredAt);
      if (!t) return;
      const who = r.teamName || [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email || 'Unknown';
      events.push({ ts: t, type: 'tournament', who,
        desc: `registered for tournament${r.tournamentName ? ` ${r.tournamentName}` : ''}${r.division ? ` (${r.division})` : ''}`,
        src: { collection: 'registrations', id: r.id, doc: r } });
    });

    // Instrumented events: trusted, server-stamped records. Their type prefix
    // maps to the same filter chips (reservation.canceled -> reservation).
    evEvents.forEach(e => {
      const t = ts(e.timestamp);
      if (!t) return;
      const prefix = (e.type || '').split('.')[0];
      const chip = ['registration', 'purchase', 'checkin', 'reservation', 'waitlist', 'tournament', 'email', 'class', 'memberBooking', 'user', 'athlete'].includes(prefix) ? prefix : 'event';
      const who = e.actor?.name || e.actor?.email || 'Unknown';
      const tgt = e.target?.name || e.target?.email || '';
      events.push({ ts: t, type: chip, who,
        desc: `${e.description || e.type}${tgt && tgt !== who ? ` — ${tgt}` : ''}`,
        src: { collection: 'events', id: e.id, doc: e } });
    });

    evActivity.forEach(a => {
      const t = ts(a.timestamp) || ts(a.createdAt) || ts(a.date);
      if (!t) return;
      events.push({ ts: t, type: 'activity', who: a.actorName || a.userName || a.email || a.actor || 'System',
        desc: a.description || a.message || a.action || 'activity recorded',
        src: { collection: 'activity', id: a.id, doc: a } });
    });

    return events
      .filter(e => e.ts && (evTypes.length === 0 || evTypes.includes(e.type)))
      .filter(e => qMatch(e.who, e.desc))
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 300);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>Logs</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          What threw, what happened, and what Stripe told us.
        </Typography>

        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, flexWrap: 'wrap' }}>
            <Tabs value={tab} onChange={(e, v) => { setTab(v); setSearch(''); }}
              variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
              sx={{ flex: 1, minWidth: 0 }}>
              <Tab label="Error Log" icon={<BugReportIcon />} iconPosition="start" />
              <Tab label="Event Log" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="Stripe" icon={<PaymentsIcon />} iconPosition="start" />
              <Tab label="Versions" icon={<NewReleasesIcon />} iconPosition="start" />
            </Tabs>
            <TextField size="small" placeholder="Search…" value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ width: 220, my: 1 }}
              InputProps={{ startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ) }} />
          </Box>
        </Paper>

        {/* ── Error Log ─────────────────────────────────────────────────── */}
        {tab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Error Logs</Typography>
              {logs.length > 0 && (
                <Button startIcon={<DeleteSweepIcon />} color="error" size="small" onClick={clearAllLogs}>
                  Clear All
                </Button>
              )}
            </Box>
            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : logs.length === 0 ? (
              <Alert severity="success">No error logs found.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.filter(l => qMatch(l.page, l.message, l.userEmail)).map(log => (
                      <TableRow key={log.id} onClick={() => setSelectedLog(log)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#fff8f8' } }}>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#666' }}>
                          {log.timestamp?.toDate
                            ? log.timestamp.toDate().toLocaleString()
                            : new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          <Typography variant="caption" display="block">{log.userEmail || '—'}</Typography>
                          <Typography variant="caption" sx={{ color: '#aaa' }}>{log.userId || ''}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.page || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: '#c0392b', fontWeight: 500, maxWidth: 200 }}>
                          {log.error || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#666', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={log.details || ''} placement="top">
                            <span>{log.details || '—'}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => deleteLog(log.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ── Event Log ─────────────────────────────────────────────────── */}
        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              {Object.entries(EV_META).map(([key, m]) => (
                <Chip
                  key={key}
                  label={m.label}
                  size="small"
                  onClick={() => setEvTypes(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key])}
                  sx={{
                    cursor: 'pointer', fontWeight: 600,
                    backgroundColor: evTypes.includes(key) ? m.bg : '#eee',
                    color: evTypes.includes(key) ? m.color : '#999',
                    border: evTypes.includes(key) ? `1px solid ${m.color}` : '1px solid #ddd',
                  }}
                />
              ))}
            </Box>

            {evLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

            {!evLoading && (() => {
              const events = buildEvents();
              if (events.length === 0) {
                return <Typography sx={{ color: '#888', py: 4, textAlign: 'center' }}>No events match the current filters.</Typography>;
              }
              return (
                <Paper variant="outlined">
                  {events.map((e, i) => {
                    const m = EV_META[e.type] || EV_META.activity;
                    const d = new Date(e.ts);
                    return (
                      <Box key={`${e.src?.collection || '?'}:${e.src?.id || i}:${e.type}`}
                        onClick={() => setSelectedEvent(e)}
                        role="button" tabIndex={0}
                        onKeyDown={(k) => { if (k.key === 'Enter' || k.key === ' ') { k.preventDefault(); setSelectedEvent(e); } }}
                        sx={{
                          display: 'flex', gap: 1.5, alignItems: 'baseline',
                          px: 2, py: 1, borderBottom: '1px solid #f0f0f0',
                          backgroundColor: i % 2 === 0 ? '#fafafa' : 'white',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#EEF3F9' },
                          '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: '-2px' },
                        }}>
                        <Typography variant="caption" sx={{ color: '#999', minWidth: 130, fontFamily: 'monospace' }}>
                          {d.toLocaleDateString()} {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Chip label={m.label} size="small" sx={{ backgroundColor: m.bg, color: m.color, fontWeight: 600, fontSize: '0.68rem', height: 20 }} />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'break-word' }}>
                          <strong>{e.who}</strong> {e.desc}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#aaa', py: 1 }}>
                    Showing the {events.length} most recent matching events (max 300) — click a row for the record behind it
                  </Typography>
                </Paper>
              );
            })()}
          </Box>
        )}

        {/* ── Stripe webhook log ────────────────────────────────────────── */}
        {tab === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Stripe Webhook Log</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={backfillWebhookLog} disabled={whLoading}>Backfill (30 days)</Button>
                <Button size="small" variant="outlined" onClick={loadWebhookLog} disabled={whLoading}>Refresh</Button>
              </Box>
            </Box>
            {whMsg && <Alert severity="info" sx={{ mb: 2 }}>{whMsg}</Alert>}
            {whLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
            {!whLoading && whEvents.length === 0 && (
              <Typography sx={{ color: '#888', py: 4, textAlign: 'center' }}>No webhook events logged yet. New Stripe events will appear here.</Typography>
            )}
            {!whLoading && whEvents.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f1f3f5' }}>
                      <TableCell>Received</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Replay</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {whEvents.filter(w => qMatch(w.type, w.source, w.status, whCustomer(w))).map((w, i) => (
                      <TableRow key={w.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {w.receivedAt?.seconds ? new Date(w.receivedAt.seconds * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{w.type || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{w.source || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{whCustomer(w) || '—'}</TableCell>
                        <TableCell align="right">{typeof w.amount === 'number' ? `$${(w.amount / 100).toFixed(2)}` : '—'}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={w.status || 'unknown'} sx={{
                            fontWeight: 600, fontSize: '0.68rem',
                            backgroundColor: w.status === 'success' ? '#E9F7EF' : w.status === 'failed' ? '#FDEDEC' : w.status === 'historical' ? '#EAF2F8' : '#FEF9E7',
                            color: w.status === 'success' ? '#1E6B3C' : w.status === 'failed' ? '#922B21' : w.status === 'historical' ? '#1A5276' : '#B7950B',
                          }} />
                          {(w.attempts || 0) > 1 && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#999' }}>{w.attempts} attempts</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {w.status === 'failed' && (
                            <Tooltip title="Re-process this event">
                              <span>
                                <IconButton size="small" color="primary"
                                  disabled={whReplaying === w.id}
                                  onClick={() => replayWebhook(w.id)}>
                                  {whReplaying === w.id ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ── Version log ───────────────────────────────────────────────── */}
        {tab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Version Log</Typography>
            {/* Said once here rather than repeated on every row: the hover
                highlight carries it after the first read, and the column it
                used to occupy was taken from the notes. */}
            <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
              Select a release to read its notes.
            </Typography>
            {verLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
            {!verLoading && versions.length === 0 && (
              <Alert severity="info">
                No releases recorded yet. This reads the <code>versionLog</code> collection,
                which nothing writes to so far — a release lands here once the deploy stamps
                one. It sits beside the error log on purpose: &ldquo;which build was live when
                this broke&rdquo; is the question the errors next door keep raising.
              </Alert>
            )}
            {!verLoading && versions.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      {/* '1px', NOT 1: in MUI's sx a numeric width of 1 or less is
                          a percentage multiplier, so `width: 1` sets 100% — the
                          opposite of shrink-to-fit, and it silently pinned this
                          table's first column to full width. A CSS length plus
                          nowrap is what collapses a cell to its content; the one
                          cell at 100% then takes everything left over. */}
                      {/* "Date", not "Released": the heading is the widest thing
                          in this column now that it collapses to its content. */}
                      <TableCell sx={{ fontWeight: 600, width: '1px', whiteSpace: 'nowrap', px: 1 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '1px', whiteSpace: 'nowrap' }}>Version</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '1px', whiteSpace: 'nowrap' }}>Commit</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '100%' }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.filter(v => qMatch(v.version, v.commit, v.notes)).map(v => (
                      <TableRow
                        key={v.id}
                        hover={!!v.changelog}
                        onClick={v.changelog ? () => setOpenRelease(v) : undefined}
                        sx={v.changelog ? { cursor: 'pointer' } : undefined}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#666', width: '1px', px: 1 }}>
                          {v.releasedAt?.seconds ? (() => {
                            const d = new Date(v.releasedAt.seconds * 1000);
                            // The year is only worth its width once it stops
                            // being the obvious one. Most rows are this year.
                            const sameYear = d.getFullYear() === new Date().getFullYear();
                            return (
                              <>
                                {d.toLocaleDateString([], sameYear
                                  ? { month: 'short', day: 'numeric' }
                                  : { year: 'numeric', month: 'short', day: 'numeric' })}
                                <Box component="span" sx={{ display: 'block', color: '#999', fontSize: '0.7rem' }}>
                                  {d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                </Box>
                              </>
                            );
                          })() : '—'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '1px', whiteSpace: 'nowrap' }}>{v.version || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', width: '1px', whiteSpace: 'nowrap' }}>
                          {(v.commit || '').slice(0, 8) || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: '#555', width: '100%', minWidth: 260 }}>{v.notes || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        <ChangelogDialog
          open={!!openRelease}
          markdown={openRelease?.changelog || ''}
          onClose={() => setOpenRelease(null)}
        />

        {/* ── Error detail ──────────────────────────────────────────────── */}
        <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Error Detail
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedLog && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Time</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedLog.timestamp?.toDate
                        ? selectedLog.timestamp.toDate().toLocaleString()
                        : new Date(selectedLog.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Page</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedLog.page || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>User</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedLog.userEmail || '—'}</Typography>
                    {selectedLog.userId && (
                      <Typography variant="caption" sx={{ color: '#aaa', display: 'block' }}>{selectedLog.userId}</Typography>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Error Message</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff8f8', borderColor: '#E74C3C' }}>
                    <Typography variant="body2" sx={{ color: '#C0392B', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {selectedLog.error || selectedLog.message || '—'}
                    </Typography>
                  </Paper>
                </Box>
                {selectedLog.details && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Details</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {selectedLog.details}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                {Object.entries(selectedLog)
                  .filter(([k]) => !['id','page','userEmail','userId','error','message','details','timestamp'].includes(k))
                  .length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Additional Fields</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8f9fa' }}>
                      {Object.entries(selectedLog)
                        .filter(([k]) => !['id','page','userEmail','userId','error','message','details','timestamp'].includes(k))
                        .map(([k, v]) => (
                          <Box key={k} sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#888', minWidth: 100, fontWeight: 600 }}>{k}:</Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(v)}</Typography>
                          </Box>
                        ))}
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button color="error" startIcon={<DeleteIcon />}
              onClick={() => { deleteLog(selectedLog.id); setSelectedLog(null); }}>
              Delete
            </Button>
            <Button onClick={() => setSelectedLog(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ── Event detail ──────────────────────────────────────────────── */}
        {/* The timeline flattens six collections into one sentence each. This
            opens the record that sentence was made from, which is the thing
            you actually need when the sentence is not enough. */}
        <Dialog open={!!selectedEvent} onClose={() => setSelectedEvent(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Event Detail
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedEvent && (() => {
              const m = EV_META[selectedEvent.type] || EV_META.activity;
              const d = new Date(selectedEvent.ts);
              const src = selectedEvent.src || {};
              const raw = src.doc || {};
              const isInstrumented = src.collection === 'events';
              const meta = raw.metadata && typeof raw.metadata === 'object' ? raw.metadata : null;
              const hidden = ['id', 'metadata', 'actor', 'target'];
              const fields = Object.entries(raw)
                .filter(([k]) => !hidden.includes(k))
                .sort(([a], [b]) => a.localeCompare(b));
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Type</Typography>
                      <Chip label={m.label} size="small"
                        sx={{ backgroundColor: m.bg, color: m.color, fontWeight: 600, fontSize: '0.68rem', height: 20, mt: 0.25 }} />
                      {isInstrumented && raw.type && (
                        <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                          {raw.type}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>When</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.toLocaleString()}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Who</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedEvent.who}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Record</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{src.collection || '—'}</Typography>
                      {src.id && (
                        <Typography variant="caption" sx={{ color: '#aaa', display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {src.id}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>What happened</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="body2"><strong>{selectedEvent.who}</strong> {selectedEvent.desc}</Typography>
                    </Paper>
                  </Box>

                  {/* Only the events collection carries a verified actor and a
                      target; the other sources are documents we inferred a
                      moment from, so there is nobody to attribute them to. */}
                  {isInstrumented && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 240px', backgroundColor: '#f8f9fa' }}>
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5, fontWeight: 700 }}>Actor</Typography>
                        <Typography variant="body2">{raw.actor?.name || '—'}</Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>{raw.actor?.email || '—'}</Typography>
                        {raw.actor?.roles?.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>{raw.actor.roles.join(', ')}</Typography>
                        )}
                        {raw.actor?.uid && (
                          <Typography variant="caption" sx={{ color: '#aaa', display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>{raw.actor.uid}</Typography>
                        )}
                      </Paper>
                      <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 240px', backgroundColor: '#f8f9fa' }}>
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5, fontWeight: 700 }}>Target</Typography>
                        <Typography variant="body2">{raw.target?.name || '—'}</Typography>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>{raw.target?.email || '—'}</Typography>
                        {raw.target?.id && (
                          <Typography variant="caption" sx={{ color: '#aaa', display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>{raw.target.id}</Typography>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {meta && Object.keys(meta).length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>Metadata</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8f9fa' }}>
                        {Object.entries(meta).map(([k, v]) => (
                          <Box key={k} sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#888', minWidth: 120, fontWeight: 600 }}>{k}:</Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {truncate(fmtValue(v))}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}

                  {fields.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                        Record fields ({src.collection}/{src.id})
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8f9fa', maxHeight: 320, overflowY: 'auto' }}>
                        {fields.map(([k, v]) => (
                          <Box key={k} sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#888', minWidth: 120, fontWeight: 600 }}>{k}:</Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {truncate(fmtValue(v))}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button
              onClick={() => {
                try {
                  navigator.clipboard.writeText(JSON.stringify(selectedEvent?.src?.doc || {}, null, 2));
                } catch (e) { /* clipboard blocked — nothing to recover, the fields are on screen */ }
              }}>
              Copy JSON
            </Button>
            <Button onClick={() => setSelectedEvent(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
